/**
 * PageTreeNode — recursive tree node component for the page hierarchy.
 * Supports expand/collapse, drag-and-drop move, and inline new-page action.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePagesContext } from '../../state/pages-context';
import type { PageTreeNode as PageTreeNodeType } from '../../state/pages-context';
import { PageTreeContextMenu } from './PageTreeContextMenu';
import * as pagesApi from '../../api/pages-api';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PageTreeNodeProps {
  readonly node: PageTreeNodeType;
  readonly depth: number;
  readonly activePageId: string | null;
  readonly libraryId: string;
  readonly onCreatePage: (parentPageId?: string | null) => void;
  readonly siblings?: readonly PageTreeNodeType[];
  readonly siblingIndex?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PageTreeNode({
  node,
  depth,
  activePageId,
  libraryId,
  onCreatePage,
  siblings,
  siblingIndex,
}: PageTreeNodeProps): React.ReactElement {
  const navigate = useNavigate();
  const { movePage, reorderPage, fetchPageTree, archivePage } = usePagesContext();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [dropZone, setDropZone] = useState<'before' | 'inside' | 'after' | null>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  // Inline rename state
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(node.title);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const startRename = useCallback(() => {
    setRenameValue(node.title);
    setIsRenaming(true);
  }, [node.title]);

  const saveRename = useCallback(async () => {
    const trimmed = renameValue.trim();
    setIsRenaming(false);
    if (trimmed && trimmed !== node.title) {
      try {
        await pagesApi.updatePage(node.pageId, { title: trimmed });
        // Refresh tree to show updated title
        fetchPageTree(libraryId);
      } catch {
        // Revert on failure — tree will re-render with original title
      }
    }
  }, [renameValue, node.title, node.pageId, libraryId, fetchPageTree]);

  const hasChildren = node.children.length > 0;
  const isActive = node.pageId === activePageId;

  const handleClick = useCallback(() => {
    if (isRenaming) return;
    navigate(`/pages/${libraryId}/${node.pageId}`);
  }, [navigate, libraryId, node.pageId, isRenaming]);

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsExpanded((prev) => !prev);
    },
    []
  );

  const handleNewChild = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCreatePage(node.pageId);
    },
    [onCreatePage, node.pageId]
  );

  const handleArchive = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        const action = await archivePage(node.pageId);
        // If the archived/deleted page was active, navigate back to library
        if (node.pageId === activePageId) {
          navigate(`/pages/${libraryId}`);
        }
        // Optionally could show a toast here with the action result
      } catch {
        // Error handled by context
      }
    },
    [archivePage, node.pageId, activePageId, libraryId, navigate]
  );

  // Drag-and-drop handlers with three-zone detection
  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      e.dataTransfer.setData('text/plain', node.pageId);
      e.dataTransfer.effectAllowed = 'move';
    },
    [node.pageId]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!nodeRef.current) return;
    const rect = nodeRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;

    // Top 25% = before, middle 50% = inside (nest), bottom 25% = after
    if (y < height * 0.25) {
      setDropZone('before');
    } else if (y > height * 0.75) {
      setDropZone('after');
    } else {
      setDropZone('inside');
    }
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropZone(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const draggedPageId = e.dataTransfer.getData('text/plain');
      const currentDropZone = dropZone;
      setDropZone(null);

      if (!draggedPageId || draggedPageId === node.pageId) return;

      if (currentDropZone === 'inside') {
        // Nest as child
        movePage(draggedPageId, node.pageId);
      } else if (currentDropZone === 'before' || currentDropZone === 'after') {
        // Reorder among siblings
        const parentId = node.parentPageId;
        const sibs = siblings ?? [];
        const idx = siblingIndex ?? 0;

        let beforeSort: string | null = null;
        let afterSort: string | null = null;

        if (currentDropZone === 'before') {
          afterSort = node.sortOrder;
          if (idx > 0 && sibs[idx - 1]) {
            beforeSort = sibs[idx - 1]!.sortOrder;
          }
        } else {
          beforeSort = node.sortOrder;
          if (idx < sibs.length - 1 && sibs[idx + 1]) {
            afterSort = sibs[idx + 1]!.sortOrder;
          }
        }

        reorderPage(draggedPageId, parentId, beforeSort, afterSort);
      }
    },
    [node.pageId, node.parentPageId, node.sortOrder, dropZone, movePage, reorderPage, siblings, siblingIndex]
  );

  const indent = depth * 16;

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined}>
      <PageTreeContextMenu
        onRename={startRename}
        onNewChild={() => onCreatePage(node.pageId)}
        onArchive={() => {
          archivePage(node.pageId).then(() => {
            if (node.pageId === activePageId) {
              navigate(`/pages/${libraryId}`);
            }
          }).catch(() => { /* Error handled by context */ });
        }}
        pageTitle={node.title}
      >
      <div
        ref={nodeRef}
        className={`relative flex items-center gap-1 py-1 px-1 rounded-md text-sm cursor-pointer transition-colors group ${
          isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-accent/50'
        } ${dropZone === 'inside' ? 'ring-2 ring-primary/50 bg-primary/5' : ''}`}
        style={{ paddingLeft: `${indent + 4}px` }}
        onClick={handleClick}
        onDoubleClick={startRename}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        draggable={!isRenaming}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drop zone indicators */}
        {dropZone === 'before' && (
          <div className="absolute top-0 left-2 right-2 h-0.5 bg-primary rounded-full" aria-hidden="true" />
        )}
        {dropZone === 'after' && (
          <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" aria-hidden="true" />
        )}
        {/* Expand/collapse toggle */}
        <button
          onClick={handleToggle}
          className={`w-4 h-4 flex items-center justify-center shrink-0 rounded hover:bg-accent ${
            !hasChildren ? 'invisible' : ''
          }`}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          tabIndex={-1}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          >
            <path d="M6 3l5 5-5 5V3z" />
          </svg>
        </button>

        {/* Page title — inline editable on double-click */}
        {isRenaming ? (
          <input
            ref={renameInputRef}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={saveRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRename();
              if (e.key === 'Escape') setIsRenaming(false);
            }}
            className="flex-1 min-w-0 px-1 py-0 text-sm bg-background border rounded focus:outline-none focus:ring-1 focus:ring-ring"
            aria-label={`Rename page ${node.title}`}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="truncate flex-1 min-w-0" title={node.title}>{node.title}</span>
        )}

        {/* New child page button (visible on hover) */}
        {isHovered && (
          <>
            <button
              onClick={handleArchive}
              className="w-5 h-5 flex items-center justify-center shrink-0 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
              aria-label={`Archive page ${node.title}`}
              title="Archive page"
              tabIndex={-1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="w-3 h-3"
                aria-hidden="true"
              >
                <path d="M2 3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1H2V3Zm0 2.5h12v7a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 12.5v-7Zm4 2a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1H6Z" />
              </svg>
            </button>
            <button
              onClick={handleNewChild}
              className="w-5 h-5 flex items-center justify-center shrink-0 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
              aria-label={`Create child page under ${node.title}`}
              title="Create child page"
              tabIndex={-1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="w-3 h-3"
                aria-hidden="true"
              >
                <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </button>
          </>
        )}
      </div>
      </PageTreeContextMenu>

      {/* Children */}
      {hasChildren && isExpanded && (
        <ul role="group">
          {node.children.map((child, idx) => (
            <PageTreeNode
              key={child.pageId}
              node={child}
              depth={depth + 1}
              activePageId={activePageId}
              libraryId={libraryId}
              onCreatePage={onCreatePage}
              siblings={node.children}
              siblingIndex={idx}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
